<script setup>
import { computed, onMounted, ref } from 'vue'
import IlluminatedButton from '@/components/buttons/IlluminatedButton.vue'
import OutlineButton from '@/components/buttons/OutlineButton.vue'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { SplitText } from 'gsap/SplitText'

gsap.registerPlugin(ScrollTrigger, SplitText)

const props = defineProps({
    upperTitle: String,
    title: String,
    content: String,
    buttonLabel: String,
    buttonLink: String,
    buttonType: {
        type: String,
        default: 'illuminated'
    },
    image: String
})



// Map buttonType to component
const buttonComponents = {
    illuminated: IlluminatedButton,
    outline: OutlineButton,
}

const ButtonComponent = computed(() => {
    return buttonComponents[props.buttonType] || IlluminatedButton
})

const container = ref(null)
const titleRef = ref(null)
const contentRef = ref(null)
const buttonRef = ref(null)

onMounted(async () => {
  const titleEl = titleRef.value;
  const contentEl = contentRef.value;
  const buttonEl = buttonRef.value;
  
  let splitChars = [];
  if (titleEl) {
    await document.fonts.ready;
    const split = new SplitText(titleEl, { type: 'words,chars' });
    splitChars = split.chars;
  }

  // Set initial states
  gsap.set(splitChars, { opacity: 0, filter: "blur(10px)", scale: 1.8 });
  if (contentEl) gsap.set(contentEl, { opacity: 0, filter: "blur(10px)" });
  if (buttonEl) gsap.set(buttonEl, { opacity: 0, y: 20, filter: "blur(10px)" });

  const tl = gsap.timeline({
    scrollTrigger: {
      trigger: container.value,
      start: '250 80%',
      end: '1000 80%',
      scrub: false,      
    },
  });

  if (splitChars.length > 0) {
    tl.to(splitChars, {
      opacity: 1,
      filter: "blur(0px)",
      scale: 1,
      stagger: 0.03,
      duration: 0.6,
      ease: 'power2.out',
    });
  }

  if (contentEl) {
    tl.to(contentEl, {
      opacity: 1,
      filter: "blur(0px)",
      duration: 0.75,
      ease: 'power2.out',
    }, '-=0.5');
  }

  if (buttonEl) {
    tl.to(buttonEl, {
      opacity: 1,
      filter: "blur(0px)",
      y: 0,
      duration: 0.75,
      ease: 'power2.out',
    }, '<0.1');
  }
});
</script>

<template>
    <section class="text-section" ref="container">
        <div class="main-content">
            <h2>
                <span v-if="upperTitle" class="text-section__upper-title accented">{{ upperTitle }}</span>
                    <span v-if="title" class="text-section__title" ref="titleRef">{{ title }}</span>
            </h2>
            <div v-if="content" class="text-section__content" v-html="content" ref="contentRef"></div>
            
            <div ref="buttonRef">
            
              <component
                  v-if="buttonLabel"
                  :is="ButtonComponent"
                  :label="buttonLabel"
                  :link="buttonLink"
              />
            </div>
        </div>
       
        <NuxtImg 
          v-if="props.image"
          format="webp" 
          :src="props.image"
          alt=""
          class="text-section__image" 
        />
        
    </section>

</template>

<style lang="scss" scoped>
.text-section {

    width: 100%;
    text-align: center;
    position: relative;


    &:before{
        content: "";
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        left: 5vw;
        width: 90vw;
        aspect-ratio: 19 / 9;
        border-radius: 1870px;
        opacity: 0.4;
        background: rgba(48, 131, 255, 0.6);
        filter: blur(250px);
    }

    &:after{
        content: "";
        position: absolute;
        background-image: url(/images/text-section-stars.webp);
        background-size: contain;
        background-repeat: no-repeat;
        background-position: center;
        top: 50%;
        left: 50%;
        width: 65vw;
        aspect-ratio: 104 / 69;
        transform: translate(-50%, -50%);
        z-index: -3;
    }

    & .main-content{
        margin: 0 auto;
        max-width: 560px;
        text-align: center;
        display: flex;
        flex-direction: column;
        justify-content: center;
        text-align: center;
        padding: 200px 30px 0;
    }

    & .text-section__title{
        color: $white;
        background: unset;
        background-clip: initial;
        -webkit-background-clip: initial;
        -webkit-text-fill-color: initial;
    }

    & .text-section__content {
        margin: 60px 0;
        color: $white;
        font-size: 17px;
        text-align: justify;
        text-align-last: center;
    }

    & .text-section__image {
        width: 100%;
        max-width: 900px;
        height: auto;
        margin: 70px auto;

    }
}

</style>